import { useState } from 'react';
import { api } from '../../api/client';
import { Can } from '../../lib/rbac';
import { secureUrl } from '../../utils/url';
import { PackageSearch, Check, ExternalLink, Loader2, Plus, AlertCircle } from 'lucide-react';

// Producto por el que pregunta el comprador (variante u otro producto). Lo propone el
// asistente; el operador SIEMPRE confirma (decisión del usuario 2026-09-24). Hasta
// confirmar, la tarjeta bloquea "Responder". Al confirmar, el asistente rehace la
// respuesta con ese producto (llega por el aviso question:draft_ready).

export interface ProductCandidate {
  asin: string;
  kind: 'variant' | 'other';
  reason: string | null;
  variant_label: string | null;
  title: string | null;
  image: string | null;
  amazon_price: number | null;
  amazon_url: string;
  our?: {
    state: 'active' | 'paused' | 'none';
    item_id: string | null;
    site_item_id: string | null;
    permalink: string | null;
    site_id: string;
  };
}

const COUNTRY: Record<string, string> = {
  MLA: 'Argentina', MLM: 'México', MLC: 'Chile', MCO: 'Colombia', MLB: 'Brasil',
  MLU: 'Uruguay', MPE: 'Perú', MEC: 'Ecuador',
};

export function QuestionProductPanel({
  questionId,
  siteId,
  sellerId,
  candidates,
  confirmedAsin,
  onConfirmed,
}: {
  questionId: number;
  siteId: string;
  sellerId: number;
  candidates: ProductCandidate[];
  confirmedAsin: string | null;
  onConfirmed: (asin: string, candidate: ProductCandidate) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [queued, setQueued] = useState<Record<string, boolean>>({});
  const [otherAsin, setOtherAsin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const country = COUNTRY[siteId] || siteId;

  const confirm = async (asin: string) => {
    setBusy(asin);
    setError(null);
    const res = await api.post<ProductCandidate>(
      `/mercadolibre/questions/${questionId}/product?seller_id=${sellerId}`,
      { asin }
    );
    setBusy(null);
    if (res.success && res.data) {
      setRegenerating(true);
      setOtherAsin('');
      onConfirmed(asin, res.data);
    } else {
      setError((res as { error?: string }).error || 'No se pudo confirmar el producto');
    }
  };

  const enqueue = async (asin: string) => {
    setBusy(`q-${asin}`);
    setError(null);
    const res = await api.post('/publisher/queue/enqueue', { asins: [asin], seller_id: sellerId });
    setBusy(null);
    if (res.success) setQueued((m) => ({ ...m, [asin]: true }));
    else setError((res as { error?: string }).error || 'No se pudo encolar');
  };

  const validOther = /^[A-Z0-9]{10}$/.test(otherAsin.trim().toUpperCase());

  return (
    <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 px-3 py-2 space-y-2">
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-orange-400">
        <PackageSearch className="w-3 h-3" />
        Producto por el que pregunta el comprador
        {confirmedAsin ? (
          <span className="ml-auto rounded bg-green-500/15 px-1.5 py-0.5 normal-case tracking-normal text-green-400">Confirmado</span>
        ) : (
          <span className="ml-auto rounded bg-amber-500/15 px-1.5 py-0.5 normal-case tracking-normal text-amber-400">Falta confirmar para responder</span>
        )}
      </div>

      {candidates.map((c) => {
        const isConfirmed = confirmedAsin === c.asin;
        const state = c.our?.state || 'none';
        return (
          <div
            key={c.asin}
            className={`flex flex-wrap items-start gap-3 rounded-md p-2 ${isConfirmed ? 'bg-green-500/10 ring-1 ring-green-500/30' : 'bg-[var(--color-bg)]'}`}
          >
            {c.image ? (
              <img src={secureUrl(c.image)} alt="" className="h-14 w-14 shrink-0 rounded object-contain bg-white" />
            ) : (
              <div className="h-14 w-14 shrink-0 rounded bg-[var(--color-border)]" />
            )}
            <div className="min-w-0 flex-1 text-xs space-y-0.5">
              <a
                href={c.amazon_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-1 text-[var(--color-text)] hover:underline break-words"
              >
                {c.title || c.asin}
                <ExternalLink className="w-3 h-3 shrink-0 mt-0.5 opacity-60" />
              </a>
              <div className="text-[var(--color-text-muted)]">
                {c.asin}
                {c.variant_label && <> · {c.variant_label}</>}
                {c.amazon_price != null && <> · US$ {c.amazon_price.toFixed(2)} en Amazon</>}
              </div>
              {c.reason && <div className="italic text-[var(--color-text-muted)]">{c.reason}</div>}
              <div>
                {state === 'active' && (
                  <span className="text-green-400">
                    Publicado y activo en {country}
                    {c.our?.permalink && (
                      <>
                        {' · '}
                        <a href={c.our.permalink} target="_blank" rel="noreferrer" className="underline">ver publicación</a>
                      </>
                    )}
                  </span>
                )}
                {state === 'paused' && <span className="text-amber-400">Lo tenemos, pero no está a la venta en {country}</span>}
                {state === 'none' && <span className="text-[var(--color-text-muted)]">No lo tenemos publicado</span>}
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <Can cap="questions.ai_draft">
                {isConfirmed ? (
                  <span className="flex items-center gap-1 text-xs text-green-400"><Check className="w-3.5 h-3.5" />Confirmado</span>
                ) : (
                  <button
                    onClick={() => confirm(c.asin)}
                    disabled={!!busy}
                    className="flex items-center gap-1 rounded bg-orange-500/15 px-2 py-1 text-xs text-orange-300 hover:bg-orange-500/25 disabled:opacity-50"
                  >
                    {busy === c.asin ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Es este
                  </button>
                )}
              </Can>
              {state === 'none' && (
                <Can cap="publisher.enqueue">
                  {queued[c.asin] ? (
                    <span className="text-xs text-sky-400">Encolado</span>
                  ) : (
                    <button
                      onClick={() => enqueue(c.asin)}
                      disabled={!!busy}
                      className="flex items-center gap-1 rounded bg-sky-500/15 px-2 py-1 text-xs text-sky-300 hover:bg-sky-500/25 disabled:opacity-50"
                    >
                      {busy === `q-${c.asin}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Encolar
                    </button>
                  )}
                </Can>
              )}
            </div>
          </div>
        );
      })}

      <Can cap="questions.ai_draft">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-[var(--color-text-muted)]">¿Es otro producto? Pegá su ASIN:</span>
          <input
            value={otherAsin}
            onChange={(e) => setOtherAsin(e.target.value.toUpperCase())}
            placeholder="B0XXXXXXXX"
            maxLength={10}
            className="w-32 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[var(--color-text)] focus:outline-none focus:border-orange-500"
          />
          <button
            onClick={() => confirm(otherAsin.trim().toUpperCase())}
            disabled={!validOther || !!busy}
            className="rounded bg-orange-500/15 px-2 py-1 text-orange-300 hover:bg-orange-500/25 disabled:opacity-40"
          >
            Usar este
          </button>
        </div>
      </Can>

      {regenerating && (
        <div className="flex items-center gap-1.5 text-xs text-violet-400">
          <Loader2 className="w-3 h-3 animate-spin" />
          El asistente está rehaciendo la respuesta con este producto (alrededor de 1 minuto).
        </div>
      )}
      {error && (
        <div className="flex items-center gap-1.5 text-xs text-red-400">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
}
